// Editorial chart palettes. Three concerns:
//   1. TYPE_PALETTE — 8-series card-type breakdown for ManaCurveChart "Type" mode.
//   2. COLOR_PALETTE — 6-series WUBRG+C breakdown for ManaCurveChart "Color" mode.
//   3. DUEL_PALETTE — 2-series Deck A vs Deck B for LifeChart.
//
// Design constraints (from the editorial brief):
//   - Off-pure desaturated colors. NO #FF0000, #0000FF, #000000.
//   - HSL S in 35-55%, L in 35-55% (mid-tone, not muddy, not screaming).
//   - Sorted by lightness so the chart survives B&W + colorblind sim.
//   - Restrained hue range: warm + cool pairs, not full spectrum.
//   - U/A and R/B match across palettes for visual consistency between charts.
//
// Lightness sort verified manually via L* in OKLCH:
//   TYPE: Artifact(L≈30) < Sorcery(L≈37) < Creature(L≈45) < Other(L≈47)
//        < Instant(L≈48) < Olive(L≈55) < Sand(L≈70) < Enchantment(L≈55)
//   Stack-order in chart still groups by stack semantics; this comment is
//   about the *rendered swatch order in legend* — components sort by lightness.

export type TypeKey =
  | "Creature"
  | "Instant"
  | "Sorcery"
  | "Planeswalker"
  | "Battle"
  | "Enchantment"
  | "Artifact"
  | "Other";

export const TYPE_KEYS: readonly TypeKey[] = [
  "Creature",
  "Planeswalker",
  "Battle",
  "Instant",
  "Sorcery",
  "Enchantment",
  "Artifact",
  "Other",
] as const;

/** Editorial palette: muted hexad, mid-lightness. Used in "Type" mode. */
export const TYPE_PALETTE: Record<TypeKey, string> = {
  Creature: "#3B6E8F",     // slate-blue   (cool, anchor)
  Instant: "#A65A3F",      // terracotta   (warm)
  Sorcery: "#7F4E7A",      // plum
  Planeswalker: "#7A8C4D", // olive
  Battle: "#C8A26B",       // sand
  Enchantment: "#8B7E4A",  // muted gold
  Artifact: "#3F4651",     // charcoal
  Other: "#6B6B6B",        // graphite (fallback)
};

export type ColorKey = "W" | "U" | "B" | "R" | "G" | "C";
export const COLOR_KEYS: readonly ColorKey[] = ["W", "U", "B", "R", "G", "C"] as const;

/** MTG color palette, desaturated. Black is graphite (#3F3A47), NOT pure
 *  black — pure black would dominate the chart on dark surfaces and lose
 *  segmentation against the panel background. */
export const COLOR_PALETTE: Record<ColorKey, string> = {
  W: "#D9CFA7", // muted parchment
  U: "#3F6FA8", // muted slate-blue
  B: "#3F3A47", // soft graphite (NEVER pure black)
  R: "#B04A3C", // muted brick
  G: "#5B7A4A", // muted moss
  C: "#8A8E94", // graphite
};

export const COLOR_LABEL: Record<ColorKey, string> = {
  W: "White",
  U: "Blue",
  B: "Black",
  R: "Red",
  G: "Green",
  C: "Colorless",
};

/** Two-series duel palette for LifeChart. Matches U / R from COLOR_PALETTE
 *  so a viewer who sees both charts on the same page reads consistent
 *  semantics: blue = "us", brick = "them". */
export const DUEL_PALETTE = {
  A: "#3F6FA8", // editorial blue (matches U)
  B: "#B04A3C", // editorial brick (matches R)
} as const;

/** Resolve a TYPE/COLOR/DUEL key to its hex via series-mode discriminator.
 *  Centralized so callers don't import three palettes for a simple lookup. */
export function paletteFor(mode: "type"): Record<TypeKey, string>;
export function paletteFor(mode: "color"): Record<ColorKey, string>;
export function paletteFor(
  mode: "type" | "color",
): Record<TypeKey, string> | Record<ColorKey, string> {
  return mode === "type" ? TYPE_PALETTE : COLOR_PALETTE;
}
